import { z } from 'zod';

import { BINARY_ANSWERS, type BinaryAnswer } from '@/constants/answers';
import { LicenceCopySource } from '@/constants/institution';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsV2RegulatoryInformation,
  institutionDetailsRegulatoryInformationSchema,
  institutionDetailsV2ActiveJurisdictionsSchema,
  institutionDetailsV2ApplicableAmlLawsSchema,
  institutionDetailsV2FinesOrCensureDetailsSchema,
  institutionDetailsV2OffshoreLicenceBranchesSchema,
  institutionDetailsV2RegulatoryInformationSchema,
} from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';
import { webUrlSchema } from '@/utils/url';

type InstitutionActiveJurisdiction = {
  country: string;
  licensedEntities?: number;
};

export type InstitutionRegulatoryInformation = {
  hasRegulatorySupervisionOrOversight?: BinaryAnswer;
  licenseOrAuthorisationRegistrationDetails: string;
  isMifidInvestmentFirm?: BinaryAnswer;
  licenceCopySource?: LicenceCopySource;
  licenceDocument?: string;
  licenceUrl: string;
  activeJurisdictions: InstitutionActiveJurisdiction[];
  amlEquivalentLegislation?: BinaryAnswer;
  applicableAmlLaws: string;
  offshoreLicence?: BinaryAnswer;
  offshoreLicenceBranches: string;
  underInvestigation?: BinaryAnswer;
  individualsInvestigatedOrCensured?: BinaryAnswer;
  findingsAgainstFirmOrIndividuals?: BinaryAnswer;
  impendingProceedings?: BinaryAnswer;
  finesOrCensureDetails: string;
};

export type InstitutionRegulatoryInformationOrDefaults = InstitutionRegulatoryInformation | Partial<InstitutionRegulatoryInformation>;

export const INSTITUTION_ACTIVE_JURISDICTION_DEFAULTS: InstitutionActiveJurisdiction = { country: '', licensedEntities: undefined };

export const INSTITUTION_REGULATORY_INFORMATION_DEFAULTS: InstitutionRegulatoryInformation = {
  licenseOrAuthorisationRegistrationDetails: '',
  licenceUrl: '',
  activeJurisdictions: [INSTITUTION_ACTIVE_JURISDICTION_DEFAULTS],
  applicableAmlLaws: '',
  offshoreLicenceBranches: '',
  finesOrCensureDetails: '',
};

export const hasAdverseRegulatoryHistory = ({
  underInvestigation,
  individualsInvestigatedOrCensured,
  findingsAgainstFirmOrIndividuals,
  impendingProceedings,
}: Pick<InstitutionRegulatoryInformationOrDefaults, 'underInvestigation' | 'individualsInvestigatedOrCensured' | 'findingsAgainstFirmOrIndividuals' | 'impendingProceedings'>) =>
  [underInvestigation, individualsInvestigatedOrCensured, findingsAgainstFirmOrIndividuals, impendingProceedings].includes('YES');

const institutionRegulatoryInformationLicenceSchema = z
  .object({
    licenseOrAuthorisationRegistrationDetails: institutionDetailsRegulatoryInformationSchema.shape.licenseOrAuthorisationRegistrationDetails,
    isMifidInvestmentFirm: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.isMifidInvestmentFirm') }),
  })
  .and(
    z.discriminatedUnion(
      'licenceCopySource',
      [
        z.object({
          licenceCopySource: z.literal(LicenceCopySource.DOCUMENT),
          licenceDocument: z.uuid(i18n.t('institutionDetails.regulatoryInformation.errors.licenceDocument')),
        }),
        z.object({
          licenceCopySource: z.literal(LicenceCopySource.URL),
          licenceUrl: webUrlSchema(i18n.t('institutionDetails.regulatoryInformation.errors.licenceUrl')),
        }),
      ],
      { error: i18n.t('institutionDetails.regulatoryInformation.errors.licenceCopySource') },
    ),
  );

// A discriminated union option can't hold the licence schema's own union, so a supervised company's licence answers are checked here
const institutionRegulatoryInformationSupervisionSchema = z
  .looseObject({
    hasRegulatorySupervisionOrOversight: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.hasRegulatorySupervisionOrOversight') }),
  })
  .superRefine((regulatoryInformation, context) => {
    if (regulatoryInformation.hasRegulatorySupervisionOrOversight !== 'YES') return;

    const { error } = institutionRegulatoryInformationLicenceSchema.safeParse(regulatoryInformation);

    for (const issue of error?.issues ?? []) context.addIssue(issue);
  });

const institutionRegulatoryInformationJurisdictionsSchema = z
  .object({
    activeJurisdictions: institutionDetailsV2ActiveJurisdictionsSchema,
  })
  .and(
    z.discriminatedUnion(
      'amlEquivalentLegislation',
      [
        z.object({
          amlEquivalentLegislation: z.literal('NO'),
        }),
        z.object({
          amlEquivalentLegislation: z.literal('YES'),
          applicableAmlLaws: institutionDetailsV2ApplicableAmlLawsSchema,
        }),
      ],
      { error: i18n.t('institutionDetails.regulatoryInformation.errors.amlEquivalentLegislation') },
    ),
  );

export const institutionRegulatoryInformationStatusSchema = institutionRegulatoryInformationSupervisionSchema.and(institutionRegulatoryInformationJurisdictionsSchema);

const institutionRegulatoryInformationOffshoreLicenceSchema = z.discriminatedUnion(
  'offshoreLicence',
  [
    z.object({
      offshoreLicence: z.literal('NO'),
    }),
    z.object({
      offshoreLicence: z.literal('YES'),
      offshoreLicenceBranches: institutionDetailsV2OffshoreLicenceBranchesSchema,
    }),
  ],
  { error: i18n.t('institutionDetails.regulatoryInformation.errors.offshoreLicence') },
);

const institutionRegulatoryInformationInvestigationsSchema = z
  .object({
    underInvestigation: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.underInvestigation') }),
    individualsInvestigatedOrCensured: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.individualsInvestigatedOrCensured') }),
    findingsAgainstFirmOrIndividuals: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.findingsAgainstFirmOrIndividuals') }),
    impendingProceedings: z.enum(BINARY_ANSWERS, { error: i18n.t('institutionDetails.regulatoryInformation.errors.impendingProceedings') }),
    finesOrCensureDetails: z.string(),
  })
  .superRefine((history, context) => {
    if (hasAdverseRegulatoryHistory(history)) {
      addFieldIssue(context, ['finesOrCensureDetails'], institutionDetailsV2FinesOrCensureDetailsSchema, history.finesOrCensureDetails);
    }
  });

export const institutionRegulatoryInformationHistorySchema = institutionRegulatoryInformationOffshoreLicenceSchema.and(institutionRegulatoryInformationInvestigationsSchema);

// Parsing drops the answers left over from an earlier choice, such as a licence URL after switching to a document
export const institutionRegulatoryInformationToInstitutionDetailsRegulatoryInformationApi = ({
  finesOrCensureDetails,
  ...regulatoryInformation
}: InstitutionRegulatoryInformation): Promise<InstitutionDetailsV2RegulatoryInformation> =>
  institutionDetailsV2RegulatoryInformationSchema.parseAsync({
    ...regulatoryInformation,
    ...(hasAdverseRegulatoryHistory(regulatoryInformation) && { finesOrCensureDetails }),
  });

export const institutionDetailsRegulatoryInformationApiToInstitutionRegulatoryInformation = (
  regulatoryInformation: InstitutionDetailsV2RegulatoryInformation,
): InstitutionRegulatoryInformation => ({
  hasRegulatorySupervisionOrOversight: regulatoryInformation.hasRegulatorySupervisionOrOversight,
  licenseOrAuthorisationRegistrationDetails:
    'licenseOrAuthorisationRegistrationDetails' in regulatoryInformation ? regulatoryInformation.licenseOrAuthorisationRegistrationDetails : '',
  isMifidInvestmentFirm: 'isMifidInvestmentFirm' in regulatoryInformation ? regulatoryInformation.isMifidInvestmentFirm : undefined,
  licenceCopySource: 'licenceCopySource' in regulatoryInformation ? regulatoryInformation.licenceCopySource : undefined,
  licenceDocument: 'licenceDocument' in regulatoryInformation ? regulatoryInformation.licenceDocument : undefined,
  licenceUrl: 'licenceUrl' in regulatoryInformation ? regulatoryInformation.licenceUrl : '',
  activeJurisdictions: regulatoryInformation.activeJurisdictions,
  amlEquivalentLegislation: regulatoryInformation.amlEquivalentLegislation,
  applicableAmlLaws: 'applicableAmlLaws' in regulatoryInformation ? regulatoryInformation.applicableAmlLaws : '',
  offshoreLicence: regulatoryInformation.offshoreLicence,
  offshoreLicenceBranches: 'offshoreLicenceBranches' in regulatoryInformation ? regulatoryInformation.offshoreLicenceBranches : '',
  underInvestigation: regulatoryInformation.underInvestigation,
  individualsInvestigatedOrCensured: regulatoryInformation.individualsInvestigatedOrCensured,
  findingsAgainstFirmOrIndividuals: regulatoryInformation.findingsAgainstFirmOrIndividuals,
  impendingProceedings: regulatoryInformation.impendingProceedings,
  finesOrCensureDetails: regulatoryInformation.finesOrCensureDetails ?? '',
});
